import { inject, Injectable } from '@angular/core';
import {
  Direction,
  EventTimeline,
  EventType,
  JoinRule,
  MatrixClient,
  MatrixEvent,
  Membership,
  Room,
  RoomMember
} from 'matrix-js-sdk';
import { Room as ModelRoom } from '../../model/room';
import { RoomHistoryVisibility as ModelRoomHistoryVisibility } from '../../model/room-history-visibility';
import { RoomMembershipState as ApiRoomMembershipState } from '../../../shared-api/roomMembershipState';
import { RoomState as ApiRoomState } from '../../../shared-api/roomState';
import { Room as ApiRoom } from '../../../shared-api/room';
import { RoomMember as ModelRoomMember } from '../../model/room-member';
import {
  RoomHistoryVisibilityEnum as ApiRoomHistoryVisibilityEnum,
  RoomJoinRuleEnum as ApiRoomJoinRuleEnum,
  RoomProperties as ApiRoomProperties
} from '../../../shared-api/roomProperties';
import { RoomMember as ApiRoomMember } from '../../../shared-api/roomMember';
import { MatrixService } from '../matrix/matrix.service';
import { RoomPowerLevel } from '../../model/room-power-level';

@Injectable({
  providedIn: 'root'
})
/**
 * Hilfsklasse für RoomService.
 * Begründung: Architekturentscheidung zur Komplexitätsreduktion, keine AFO.
 */
export class RoomHelperService {
  private readonly matrix = inject(MatrixService);

  static toModelRoom(matrixRoom: Room): ModelRoom {
    return {
      ...RoomHelperService.toApiRoom(matrixRoom),
      members: matrixRoom.getMembers().map(RoomHelperService.toModelRoomMember),
      historyVisibility: matrixRoom.getHistoryVisibility() as unknown as ModelRoomHistoryVisibility,
      tags: matrixRoom.tags,
      creator: matrixRoom.getCreator(),
      avatarUrl: matrixRoom.getMxcAvatarUrl(),
      powerLevels:
        (matrixRoom
          .getLiveTimeline()
          .getState(EventTimeline.FORWARDS)
          ?.getStateEvents(EventType.RoomPowerLevels, '')
          ?.getContent() as RoomPowerLevel) ?? {},
      pinnedEventIds: RoomHelperService.extractPinnedEventIds(matrixRoom)
    };
  }

  static toModelRoomMember(member: RoomMember): ModelRoomMember {
    return {
      ...RoomHelperService.toApiRoomMember(member),
      name: member.name,
      events: member.events,
      rawDisplayName: member.rawDisplayName,
      typing: member.typing,
      powerLevel: member.powerLevel,
      avatarUrl: member.user?.avatarUrl
    };
  }

  static toApiRoom(matrixRoom: Room): ApiRoom {
    return {
      roomId: matrixRoom.roomId,
      isEncrypted: matrixRoom.hasEncryptionStateEvent(),
      roomVersion: matrixRoom.getVersion(),
      name: matrixRoom.name,
      members: matrixRoom.getMembers().map(RoomHelperService.toApiRoomMember),
      theme: RoomHelperService.getTopic(matrixRoom),
      roomAccess: matrixRoom.getJoinRule() === JoinRule.Public ? 'public' : 'private',
      joinRule: RoomHelperService.getApiJoinRule(matrixRoom),
      historyVisibility: RoomHelperService.getApiHistoryVisibility(matrixRoom),
      isFederated: RoomHelperService.getIsFederated(matrixRoom)
    };
  }

  static toApiMembershipState(type: string): ApiRoomMembershipState {
    switch (type) {
      case 'invite':
        return ApiRoomMembershipState.Invite;
      case 'join':
        return ApiRoomMembershipState.Join;
      case 'ban':
        return ApiRoomMembershipState.Ban;
      case 'knock':
        return ApiRoomMembershipState.Knock;
      case 'leave':
        return ApiRoomMembershipState.Leave;
      default:
        throw new Error(`Unknown KnownMembership type: ${type}`);
    }
  }

  static toApiRoomProperties(room: Room): ApiRoomProperties {
    return {
      name: room.name,
      theme: RoomHelperService.getTopic(room),
      isEncrypted: room.hasEncryptionStateEvent(),
      roomVersion: room.getVersion(),
      roomAccess: room.getJoinRule() === JoinRule.Public ? 'public' : 'private',
      joinRule: RoomHelperService.getApiJoinRule(room),
      historyVisibility: RoomHelperService.getApiHistoryVisibility(room),
      isFederated: RoomHelperService.getIsFederated(room),
      roomId: room.roomId
    };
  }

  static getTopic(room: Room): string {
    return room.getLiveTimeline().getState(Direction.Forward)?.getStateEvents(EventType.RoomTopic)?.pop()?.getContent()[
      'topic'
    ];
  }

  static stateEventsOnly(event: MatrixEvent): boolean {
    return event.getType() === 'm.room.state';
  }

  static eventToApiRoomState(event: MatrixEvent): ApiRoomState {
    return {
      eventId: event.getId() ?? '',
      roomId: event.getRoomId() ?? '',
      type: event.getType(),
      sender: event.getSender(),
      content: event.getContent()?.['body']
    };
  }

  static calculateDaysSinceLastEvent(currentDate: Date, lastMessageDate: Date): number {
    const timeDiff = currentDate.getTime() - lastMessageDate.getTime();
    return Math.trunc(timeDiff / 2592000000 /*1000 * 3600 * 24 * 30*/);
  }

  // hier als freie Funktion.
  static getReadUpTo(roomId: string | undefined, event: MatrixEvent, client: MatrixClient): string[] {
    if (!roomId) {
      throw new Error('Required parameter roomId was null or undefined when calling getUsersUpToRead.');
    }
    const room = client.getRoom(roomId);
    if (!room) {
      throw new Error(`No room was found for roomId ${roomId} when calling getUsersUpToRead.`);
    }
    if (!event) {
      throw new Error(`Required parameter event was null or undefined when calling getUsersUpToRead.`);
    }

    return room.getUsersReadUpTo(event);
  }

  static downloadFile(name: string, blob: Blob): void {
    if (name && blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute('download', name);
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }

  private static extractPinnedEventIds(matrixRoom: Room): string[] {
    const pinnedEvents = matrixRoom
      .getLiveTimeline()
      .getState(EventTimeline.FORWARDS)
      ?.getStateEvents(EventType.RoomPinnedEvents);
    if (!pinnedEvents || pinnedEvents.length === 0) return [];
    const ids = pinnedEvents.flatMap(event => {
      const content = event.getContent() ?? {};
      return (content['pinned'] as string[]) ?? [];
    });
    return [...new Set(ids)];
  }

  /**
   * Liest die Matrix-State-Eigenschaft m.room.join_rules und mappt sie auf das API-Enum.
   * Werte aus matrix-js-sdk und API-Enum sind stringgleich; ungültige/fehlende Werte → undefined.
   */
  private static getApiJoinRule(matrixRoom: Room): ApiRoomJoinRuleEnum | undefined {
    const rule = matrixRoom.getJoinRule() as string | undefined;
    const allowed: ApiRoomJoinRuleEnum[] = ['public', 'knock', 'invite', 'private', 'restricted', 'knock_restricted'];
    return rule && (allowed as string[]).includes(rule) ? (rule as ApiRoomJoinRuleEnum) : undefined;
  }

  /**
   * Liest die Matrix-State-Eigenschaft m.room.history_visibility und mappt sie auf das API-Enum.
   */
  private static getApiHistoryVisibility(matrixRoom: Room): ApiRoomHistoryVisibilityEnum | undefined {
    const visibility = matrixRoom.getHistoryVisibility() as string | undefined;
    const allowed: ApiRoomHistoryVisibilityEnum[] = ['invited', 'joined', 'shared', 'world_readable'];
    return visibility && (allowed as string[]).includes(visibility)
      ? (visibility as ApiRoomHistoryVisibilityEnum)
      : undefined;
  }

  /**
   * Liest m.federate aus dem m.room.create-State-Event. Laut Matrix-Spec ist der Default true.
   * Fehlt das Create-Event komplett, wird undefined zurückgegeben.
   */
  private static getIsFederated(matrixRoom: Room): boolean | undefined {
    const createEvent = matrixRoom
      .getLiveTimeline()
      .getState(EventTimeline.FORWARDS)
      ?.getStateEvents(EventType.RoomCreate, '');
    if (!createEvent) return undefined;
    const content = createEvent.getContent() as { 'm.federate'?: boolean };
    return content['m.federate'] ?? true;
  }

  // Aufgrund von Abhängigkeiten der beiden Services RoomService und ChatMessageService ist es nicht möglich diese
  // Methode innerhalb des RoomServices zu "bespielen". Da diese Methdoe aber logisch zum RoomService gehört steht sie

  private static toApiRoomMember(member: RoomMember): ApiRoomMember {
    return {
      mxid: member.userId,
      membershipState: member.membership
        ? RoomHelperService.toApiMembershipState(member.membership)
        : ApiRoomMembershipState.Leave
    };
  }

  getRoomMembership(roomId: string): Membership | undefined {
    const client = this.matrix.getClientSync();
    if (!client) {
      console.warn('Kein Client', roomId);
      return undefined;
    }

    const room = client.getRoom(roomId);
    if (!room) {
      console.warn('Kein Room', roomId);
      return undefined;
    }

    return room.getMyMembership();
  }

  getRoomname(roomId: string | undefined, roomname: string | undefined): string | undefined {
    const client = this.matrix.getClientSync();

    if (!client) {
      console.warn('Kein Client', roomId);
      return undefined;
    }
    const room = client.getRoom(roomId);
    if (!room) {
      throw new Error('No room found');
    }

    const members = room?.getMembers() ?? [];

    const otherMembers = members.filter(member => member.userId !== client.getUserId());

    if (roomname) {
      return roomname;
    }

    if (members.length === 2 && !roomname) {
      return otherMembers[0]?.rawDisplayName ?? '';
    }

    if (members.length > 2 && !roomname) {
      const names = otherMembers.map(member => member.rawDisplayName).filter(Boolean);
      if (names.length > 3) {
        return `Chat mit ${names.slice(0, 3).join(', ')} und weitere`;
      }

      return `Chat mit ${this.formatNames(names)}`;
    }

    return members[0]?.rawDisplayName ?? '';
  }

  /********************************************************************************************************************
   * Der Name des Raumes wird passend formatiert und angezeigt
   ********************************************************************************************************************/

  private formatNames(names: string[]): string {
    if (names.length === 0) return '';
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} und ${names[1]}`;

    return `${names.slice(0, -1).join(', ')} und ${names.at(-1)}`;
  }
}
